import { SortableHeader } from '@/components/search/SortableHeader'
import React, { useEffect, useMemo, useState } from 'react'
import { useModal } from '@/components/modal/Modal'
import { StaticPaginatedTable } from '@/components/search/StaticPaginatedTable'
import { Spinner } from '@/components/spinner'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { BackButton } from '@/components/layout/NavBar'
import { useAppContext } from '@/context/app'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'
import { SearchInput } from '@/components/search/SearchInput'
import { useSearchParams } from 'react-router-dom'

import { InventoryItemEditPopup, InventoryItemRemovePopup, InventoryItemRow } from './common'

const InventoryItemMenuPopup = ({
  onClose,
  row,
  onReload,
}: {
  onClose: () => void
  row: InventoryItemRow
  onReload: () => void
}) => {
  const { openModal } = useModal()

  const menu: Menu = {
    title: row.inventoryNumber || row.serialNumber || row.id,
    rows: [
      {
        icon: 'svgfont-edit',
        title: 'elements.table.inventory_items.menu.edit.edit_row',
        action: () => openModal((props) => <InventoryItemEditPopup {...props} inventoryItemId={row.id} onSaved={onReload} />),
      },
      {
        icon: 'svgfont-remove',
        title: 'elements.table.inventory_items.menu.edit.remove',
        action: () => openModal((props) => <InventoryItemRemovePopup {...props} item={row} onRemoved={onReload} />),
      },
    ],
  }

  return <MenuPopupBody menu={menu} onClose={onClose} />
}

export const CommunalRemanentInventoryItemsManagerPage = () => {
  const { services, session, translates } = useAppContext()
  const { openModal } = useModal()
  const [searchParams] = useSearchParams()
  const [items, setItems] = useState<InventoryItemRow[] | null>(null)
  const [groupsMap, setGroupsMap] = useState<Record<string, string>>({})
  const [locationsMap, setLocationsMap] = useState<Record<string, string>>({})
  const [search, setSearch] = useState(() => searchParams.get('search') || '')
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('search') || '')

  const loadData = async () => {
    const [itemsResponse, groupsResponse, locationsResponse] = await Promise.all([
      services.session.fetchWithToken('/api/communal/remanent/inventory-items'),
      services.session.fetchWithToken('/api/communal/remanent/inventory-groups'),
      services.session.fetchWithToken('/api/communal/remanent/locations'),
    ])

    if (itemsResponse.status !== 200) {
      setItems([])
    } else {
      const data = await itemsResponse.json()
      setItems(Array.isArray(data) ? data : [])
    }

    if (groupsResponse.status === 200) {
      const data = await groupsResponse.json()
      const nextMap = (Array.isArray(data) ? data : []).reduce((acc, row) => {
        acc[row.id] = row.name
        return acc
      }, {} as Record<string, string>)
      setGroupsMap(nextMap)
    } else {
      setGroupsMap({})
    }

    if (locationsResponse.status === 200) {
      const data = await locationsResponse.json()
      const nextMap = (Array.isArray(data) ? data : []).reduce((acc, row) => {
        acc[row.id] = row.name
        return acc
      }, {} as Record<string, string>)
      setLocationsMap(nextMap)
    } else {
      setLocationsMap({})
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    const nextSearch = searchParams.get('search') || ''
    setSearch(nextSearch)
    setDebouncedSearch(nextSearch)
  }, [searchParams])

  const filtered = useMemo(() => {
    if (!items) {
      return null
    }
    if (!debouncedSearch) {
      return items
    }

    const phrase = debouncedSearch.toLocaleLowerCase()
    return items.filter((item) => {
      const groupName = item.groupId == null ? '' : (groupsMap[item.groupId] || '')
      const locationName = item.locationId == null ? '' : (locationsMap[item.locationId] || '')
      return [
        item.id,
        item.inventoryNumber || '',
        item.serialNumber || '',
        item.status || '',
        groupName,
        locationName,
      ].some((value) => value.toLocaleLowerCase().includes(phrase))
    })
  }, [items, debouncedSearch, groupsMap, locationsMap])

  if (!session || !translates || !items) {
    return <Spinner />
  }

  return <LoggedInPageLayout
    pageName='inventory-items-manager'
    services={services}
    session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}
  >
    <StaticPaginatedTable<InventoryItemRow>
      sortValues={{
        group: row => row.groupId == null ? null : groupsMap[row.groupId],
        location: row => row.locationId == null ? null : locationsMap[row.locationId],
        inventoryNumber: row => row.inventoryNumber,
        serialNumber: row => row.serialNumber,
        status: row => translates.for(`elements.table.inventory_items.statuses.${row.status || 'active'}`)
      }}
      data={filtered || []}
      translates={translates}
      toolboxLeft={() => <>
        <div
          className='tool'
          onClick={() => openModal((props) => <InventoryItemEditPopup {...props} onSaved={loadData} />)}
        >
          <i className='svgfont-add' />
        </div>
      </>}
      toolboxRight={() => <>
        <SearchInput translates={translates} value={search} onChange={setSearch} onDebouncedChange={setDebouncedSearch} />
      </>}
      header={(query, onSort) => <>
        <SortableHeader field='group' query={query} onSort={onSort}>{translates.for('elements.table.inventory_items.group')}</SortableHeader>
        <SortableHeader field='location' query={query} onSort={onSort}>{translates.for('elements.table.inventory_items.location')}</SortableHeader>
        <SortableHeader field='inventoryNumber' query={query} onSort={onSort}>{translates.for('elements.table.inventory_items.inventory_number')}</SortableHeader>
        <SortableHeader field='serialNumber' query={query} onSort={onSort}>{translates.for('elements.table.inventory_items.serial_number')}</SortableHeader>
        <SortableHeader field='status' query={query} onSort={onSort}>{translates.for('elements.table.inventory_items.status')}</SortableHeader>
        <div className='hcol'></div>
      </>}
      row={(row) => <>
        <div className='col'>{row.groupId == null ? translates.for('elements.table.inventory_items.empty_group') : (groupsMap[row.groupId] || '-')}</div>
        <div className='col'>{row.locationId == null ? translates.for('elements.table.inventory_items.empty_location') : (locationsMap[row.locationId] || '-')}</div>
        <div className='col'>{row.inventoryNumber || '-'}</div>
        <div className='col'>{row.serialNumber || '-'}</div>
        <div className='col'>{translates.for(`elements.table.inventory_items.statuses.${row.status || 'active'}`)}</div>
        <div className='col icocol' onClick={() => openModal((props) => <InventoryItemMenuPopup {...props} row={row} onReload={loadData} />)}>
          <i className='svgfont-menu' />
        </div>
      </>}
    />
  </LoggedInPageLayout>
}
